import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion, requestIdleCallback } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';
import { SearchUpdateEvent } from '@shopify/events';

function cancelScheduledIdleCallback(callbackId) {
  if (typeof window.cancelIdleCallback === 'function') {
    window.cancelIdleCallback(callbackId);
  } else {
    window.clearTimeout(callbackId);
  }
}

class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  #activeFetch = null;

  #emptyStateLoaded = false;

  #idleHandle = null;

  #emptyStateGeneration = 0;

  #emptyStatePromise = null;

  #resetScrollRequested = false;

  #isComposing = false;

  #lastSearchTerm = '';

  get dialog() {
    return this.closest('dialog-component');
  }
  connectedCallback() {
    super.connectedCallback();

    if (this.#controller.signal.aborted) this.#controller = new AbortController();
    const { dialog } = this;
    const { signal } = this.#controller;
    const { searchInput } = this.refs;

    this.#lastSearchTerm = searchInput?.value?.trim() ?? '';

    if (searchInput) {

      searchInput.addEventListener('compositionstart', () => { this.#isComposing = true; }, { signal });
      searchInput.addEventListener('compositionend', (event) => {
        this.#isComposing = false;
        this.search(event);
      }, { signal });
      window.addEventListener(
        'keydown',
        (event) => {
          if (
            event.target === searchInput &&
            (event.isComposing || this.#isComposing || event.keyCode === 229) &&
            event.key === 'Enter'
          ) {
            event.stopImmediatePropagation();
          }
        },
        { signal, capture: true }
      );
    }

    if (searchInput && searchInput.value.length > 0) {
      this.#showResetButton();
    }

    this.#syncResultsA11y();

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal, once: true });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    if (RecentlyViewed.getProducts().length > 0) {
      this.#scheduleEmptyStatePrewarm();
    }
  }

  #scheduleEmptyStatePrewarm() {
    if (this.#idleHandle !== null || this.#emptyStateLoaded || this.#emptyStatePromise) return;
    this.#idleHandle = requestIdleCallback(() => {
      this.#idleHandle = null;

      this.#loadEmptyState(false)?.catch(() => {});
    });
  }

  #cancelEmptyStatePrewarm() {
    if (this.#idleHandle === null) return;

    cancelScheduledIdleCallback(this.#idleHandle);
    this.#idleHandle = null;
  }

  #handleModalClick = (event) => {
    const target =   (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();

    this.#cancelEmptyStatePrewarm();
    this.#emptyStateGeneration += 1;
    this.#activeFetch?.abort();
    this.#activeFetch = null;
    this.#emptyStatePromise = null;
    this.#resetScrollRequested = false;
    this.#emptyStateLoaded = false;
    this.#isComposing = false;
    this.#lastSearchTerm = '';
  }

  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  #handleDialogClose = () => {

    this.#resetSearch().catch(() => {});
  };

  #handleDialogOpen = () => {

    this.#cancelEmptyStatePrewarm();

    if (this.#emptyStatePromise) {

      this.#resetScrollRequested ||= true;
      return;
    }

    if (this.#emptyStateLoaded) {

      this.#resetScrollPositions();
      return;
    }

    if (RecentlyViewed.getProducts().length > 0) this.#loadEmptyState(true)?.catch(() => {});
  };

  #loadEmptyState(resetScroll) {

    if (resetScroll) this.#resetScrollRequested = true;
    if (this.#emptyStateLoaded) return Promise.resolve();
    if (this.#emptyStatePromise) return this.#emptyStatePromise;

    const generation = this.#emptyStateGeneration;
    this.#emptyStatePromise = this.#resetSearch({ generation, resetScroll: false })
      .then(() => {
        if (generation === this.#emptyStateGeneration) {
          this.#emptyStateLoaded = true;
        }
      })
      .catch((error) => {
        if (generation === this.#emptyStateGeneration) {
          this.#emptyStateLoaded = false;
        }
        throw error;
      })
      .finally(() => {
        if (generation === this.#emptyStateGeneration) {
          this.#emptyStatePromise = null;
        }
      });

    return this.#emptyStatePromise;
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return   (allItems);
  }

  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    const items = this.#allResultsItems;
    if (!items?.length) return;

    let activeItem = null;

    items.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of items.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    this.#setActiveDescendant(activeItem);

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.refs.searchInput.focus();
  }

  #setActiveDescendant(activeItem) {
    const { searchInput } = this.refs;

    if (activeItem?.id) searchInput.setAttribute('aria-activedescendant', activeItem.id);
    else searchInput.removeAttribute('aria-activedescendant');
  }

  #syncResultsA11y() {
    const items = this.#allResultsItems;
    const { searchInput } = this.refs;

    items.forEach((item, index) => {
      if (item.getAttribute('role') !== 'option') item.setAttribute('role', 'option');
      if (!item.id) item.id = `predictive-search-option-slide-${index}`;
    });

    searchInput.setAttribute('aria-expanded', items.length > 0 ? 'true' : 'false');
    if (!items.length) this.#setActiveDescendant(null);
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {

      this.#resetSearch().catch(() => {});
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const fromSearchInput = event.target === this.refs.searchInput;

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        if (!fromSearchInput) return;
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'ArrowUp':
        if (!fromSearchInput) return;
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        if (!fromSearchInput) return;
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', this.refs.searchInput.value);
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#lastSearchTerm = '';
    this.#resetSearch().catch(() => {});
  }, 100);

  search = debounce((event) => {

    if (this.#isComposing || event?.isComposing) return;

    const searchTerm = this.refs.searchInput.value.trim();
    const valueChanged = searchTerm !== this.#lastSearchTerm;

    if (!event?.inputType && !valueChanged) return;

    this.#lastSearchTerm = searchTerm;
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch().catch(() => {});
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    const deferredPromise = SearchUpdateEvent.createPromise();

    this.dispatchEvent(
      new SearchUpdateEvent({
        search: {
          query: searchTerm,
        },
        promise: deferredPromise.promise,
      })
    );

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url, abortController.signal)
      .then((resultsMarkup) => {
        if (!resultsMarkup) {
          deferredPromise.resolve({ totalCount: 0 });
          return;
        }

        if (abortController.signal.aborted) {
          deferredPromise.reject(new Error('Fetch aborted by user'));
          return;
        }

        morph(predictiveSearchResults, resultsMarkup);

        this.#syncResultsA11y();
        this.#resetScrollPositions();

        const resultCount = predictiveSearchResults.querySelectorAll('[ref="resultsItems[]"]').length;
        deferredPromise.resolve({ totalCount: resultCount });
      })
      .catch((error) => {
        deferredPromise.reject(error);
        if (abortController.signal.aborted) return;

        this.#showSearchError();
      });
  }

  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(  (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #showSearchError() {
    const dialogEl = this.dialog?.querySelector('dialog') ?? this.dialog;
    const isDialogOpen = !this.dialog || dialogEl?.hasAttribute('open') || dialogEl?.open || this.#resetScrollRequested;
    if (!isDialogOpen) return;

    const { predictiveSearchResults } = this.refs;
    if (!predictiveSearchResults) return;

    const message = Theme.translations?.search_error || 'Search is currently unavailable. Please try again.';

    const listbox = document.createElement('div');
    listbox.id = 'predictive-search-results';
    listbox.className = 'predictive-search-dropdown';
    listbox.setAttribute('role', 'listbox');
    listbox.setAttribute('aria-label', Theme.translations?.search_results_label || 'Search results');
    const inner = document.createElement('div');
    inner.className = 'predictive-search-results__inner';
    const notice = document.createElement('div');
    notice.className = 'predictive-search-results__no-results predictive-search-results__error';
    const text = document.createElement('p');
    text.textContent = message;
    notice.append(text);
    inner.append(notice);
    listbox.append(inner);
    predictiveSearchResults.replaceChildren(listbox);
    this.#syncResultsA11y();
    this.#announce(message);
  }

  #statusRegion = null;

  #announce(message) {
    let region = this.#statusRegion;
    if (!region?.isConnected) {
      region = document.createElement('div');
      region.className = 'visually-hidden';
      region.setAttribute('role', 'status');
      this.append(region);
      this.#statusRegion = region;
    }
    region.textContent = '';
    requestAnimationFrame(() => {
      region.textContent = message;
    });
  }

  #resetSearch = async ({ generation = this.#emptyStateGeneration, resetScroll = true } = {}) => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#lastSearchTerm = '';
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    try {
      const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url, abortController.signal);
      const parsedEmptySectionMarkup = new DOMParser()
        .parseFromString(emptySectionMarkup, 'text/html')
        .querySelector('.predictive-search-empty-section');

      if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

      const viewedProducts = RecentlyViewed.getProducts();

      if (viewedProducts.length > 0) {
        const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
        if (!recentlyViewedMarkup) return;

        const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
        const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
        if (!recentlyViewedProductsHtml) return;

        for (const child of recentlyViewedProductsHtml.children) {
          if (child instanceof HTMLElement) {
            child.setAttribute('ref', 'recentlyViewedWrapper');
          }
        }

        const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
        if (!collectionElement) return;
        collectionElement.prepend(...recentlyViewedProductsHtml.children);
      }

      if (abortController.signal.aborted) return;

      if (generation !== this.#emptyStateGeneration) return;

      morph(predictiveSearchResults, parsedEmptySectionMarkup);
      this.#syncResultsA11y();

      const shouldResetScroll = resetScroll || this.#resetScrollRequested;
      this.#resetScrollRequested = false;
      if (shouldResetScroll) this.#resetScrollPositions();
    } catch (error) {
      if (abortController.signal.aborted) return;
      if (generation !== this.#emptyStateGeneration) return;

      this.#showSearchError();
      throw error;
    }
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
